import React, { useEffect, useState } from 'react';
import './App.css';
import { Smile, Languages, BarChart, Zap, Video, Cpu, Mail, Linkedin, Instagram, Facebook, ArrowUp } from 'lucide-react'; 

function App() {
  // Backend එක නැති නිසා Data මෙහි සෘජුවම ඇතුළත් කර ඇත
  const [features] = useState([
    {
        id: 1,
        title: "Facial Emotion Detection",
        description: "Identifies user emotions and expressions (happy, sad, neutral) using DeepFace to evaluate engagement and confidence.",
        icon: "😊"
    },
    {
        id: 2,
        title: "Multilingual Speech Analysis",
        description: "Assess pronunciation clarity and fluency in English, German, and French using the Whisper AI API.",
        icon: "🎙️"
    },
    {
        id: 3,
        title: "Real-time Feedback",
        description: "Provides instant guidance through voice and emoji-based visual feedback to correct tone and expressions immediately.",
        icon: "⚡"
    }
  ]);

  const [currentEmoji, setCurrentEmoji] = useState('😃');
  const [showButton, setShowButton] = useState(false); 
  
  useEffect(() => {
    document.title = "Orato - AI Public Speaking Trainer";
    
    const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    link.type = 'image/png';
    link.rel = 'shortcut icon';
    link.href = '/orato_logo.png';
    document.getElementsByTagName('head')[0].appendChild(link);

    const checkScrollHeight = () => {
      if (!showButton && window.pageYOffset > 300) {
        setShowButton(true);
      } else if (showButton && window.pageYOffset <= 300) {
        setShowButton(false);
      }
    };

    window.addEventListener('scroll', checkScrollHeight);
    return () => window.removeEventListener('scroll', checkScrollHeight);
  }, [showButton]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const teamMembers = [
    { name: "Kavishka Amaranayaka", role: "Project Lead (Leader)", img: "/team_1.png" },
    { name: "Anjana Indumini", role: "Documentation & Project Manager", img: "/team_2.jpg" },
    { name: "Arindu Dinuwara", role: "Project Planning & Quality Assurance", img: "/team_3.jpg" },
    { name: "Nadhir Noori", role: "System Design & Backend Development", img: "/team_4.jpg" },
    { name: "G.H.H.R. Silva", role: "Implementation & Tooling Expert", img: "/team_5.jpg" },
    { name: "Kenan Aponso", role: "UI/UX Design & Planning", img: "/team_6.jpg" },
  ];

  useEffect(() => {
    const emojis = ['😃', '😐', '😬', '😎', '😮'];
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % emojis.length;
      setCurrentEmoji(emojis[index]);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="App">
      <nav>
        <div className="logo">
          <img src="/orato_logo.png" alt="Orato Logo" style={{ height: '40px', marginRight: '10px' }} /> 
          Orato<span>.lk</span>
        </div>
        <div className="nav-links">
          <a href="#features">Features</a>
          <a href="#how-it-works">How it Works</a>
          <a href="#about-us">About Us</a>
          <a href="#team">Team</a>
          <a href="#contact">Contact</a>
        </div>
        <button className="btn-primary">Try Demo</button>
      </nav>

      <section className="hero">
        <div className="hero-logo-large">
            <img src="/orato_logo.png" alt="Orato Logo Watermark" />
        </div>
        <div className="hero-content">
          <h1>Master Public Speaking with <span style={{color: '#a78bfa'}}>AI Feedback</span></h1>
          <p>Orato analyzes your emotions, speech patterns, and confidence in real-time. Practice alone, improve instantly, and speak to the world without fear.</p>
          <button className="btn-primary" style={{padding: '1rem 2rem', fontSize: '1.1rem'}}>Start Practice Session</button>
        </div>
        <div className="hero-visual">
          <div className="mock-screen">
            <div className="face-box"></div> 
            <div className="ai-overlay">
              <span>Emotion:</span>
              <div className="emoji-feedback">{currentEmoji}</div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="features">
        <h2 className="section-title">Why Choose Orato?</h2>
        <div className="features-grid">
          {features.map((feature, index) => (
            <div className="feature-card" key={index}>
              <div className="feature-icon">
                {feature.icon === '😊' ? <Smile size={40} /> : 
                 feature.icon === '🎙️' ? <Languages size={40} /> : 
                 feature.icon === '⚡' ? <Zap size={40} /> : <BarChart size={40} />}
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="about-us" className="about-us-vision-mission"> 
        <h2 className="section-title">About Orato Project</h2>
        <div className="about-us-grid">
          <div className="vision-card">
            <h3>Our Vision</h3>
            <p>To create a future where every individual can express their ideas to the world with full confidence, free from fear and doubt, mastering the art of communication.</p>
          </div>
          <div className="mission-card">
            <h3>Our Mission</h3>
            <p>To dedicate ourselves to elevating everyone's presentation skills and professional standards by providing real-time, unbiased feedback using an AI-powered robotic assistant that overcomes language barriers.</p>
          </div>
        </div>
      </section>

      <section id="team" className="team">
        <h2 className="section-title">Meet the Team</h2>
        <div className="team-grid">
          {teamMembers.map((member, index) => (
            <div className="team-card" key={index}>
              <div className="team-photo-container">
                <img src={member.img} alt={member.name} className="team-photo" />
              </div>
              <h3>{member.name}</h3>
              <p className="team-role">{member.role}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="contact" className="contact">
        <h2 className="section-title">Get in Touch</h2>
        <div className="contact-links">
          <a href="mailto:info.otarolk@gmail.com" className="contact-link-item"><Mail /><span>info.otarolk@gmail.com</span></a>
          <a href="https://linkedin.com/in/orato-lk-438680395" target="_blank" rel="noreferrer" className="contact-link-item"><Linkedin /><span>LinkedIn</span></a>
          <a href="https://www.instagram.com/orato.lk" target="_blank" rel="noreferrer" className="contact-link-item"><Instagram /><span>Instagram</span></a>
          <a href="https://www.facebook.com/profile.php?id=61584209788465" target="_blank" rel="noopener noreferrer" className="contact-link-item"><Facebook /><span>Facebook</span>
          </a>
        </div>
      </section>

      <footer>
        <p>© 2025 Orato.lk - All Rights Reserved.</p>
      </footer>

      <button className={`to-top-button ${showButton ? 'show' : ''}`} onClick={scrollToTop}><ArrowUp size={24} /></button>
    </div>
  );
}

export default App;